import { useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { getIDFromUrl } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { Workspace } from '@rossum/api-client/workspaces';
import { FilterSelect } from '@rossum/components/filtering/components';
import { createSettings } from '@rossum/components/filtering/helpers';
import { pageSizeOptions } from '../../../constants/values';
import { groupRoles, Groups } from '../../../types/group';

const isActiveOptions = ['true', 'false'] as const;

export const usersQueryParser = z.object({
  pageSize: z.coerce
    .number()
    .refine(num => R.isIncludedIn(num, pageSizeOptions))
    .optional()
    .default(pageSizeOptions[0])
    .catch(pageSizeOptions[0]),
  page: z.coerce.string().optional().default('1'),
  search: z.coerce.string().optional().default(''),
  role: z.coerce.number().optional(),
  isActive: z.enum(isActiveOptions).optional(),
  queue: z.coerce.number().optional(),
});

export type UsersQuery = Partial<z.infer<typeof usersQueryParser>>;

type UseUsersFilterSettingsProps = {
  groups: Groups;
  queues: Queue[] | undefined;
  workspaces: Workspace[] | undefined;
  isQueuesLoading: boolean;
  setFilters: (state: UsersQuery) => void;
};

export const useUsersFilterSettings = ({
  groups,
  queues,
  workspaces,
  isQueuesLoading,
  setFilters,
}: UseUsersFilterSettingsProps) => {
  const intl = useIntl();

  const roleOptions = useMemo(() => {
    return groups
      .filter(group => groupRoles.includes(group.name))
      .map(group => group.id.toString());
  }, [groups]);

  const workspaceOptions = useMemo(() => {
    return workspaces
      ? workspaces.map(w => ({
          id: w.id.toString(),
          label: w.name,
          options: w.queues.map(q => getIDFromUrl(q).toString()),
        }))
      : undefined;
  }, [workspaces]);

  const getStatusLabel = useMemo(
    () => (value: string) =>
      intl.formatMessage({
        id:
          value === 'true'
            ? 'containers.settings.users.status.enabled'
            : 'containers.settings.users.status.disabled',
      }),
    [intl]
  );

  const settings = useMemo(
    () => [
      createSettings({
        filterKey: 'role',
        name: intl.formatMessage({
          id: 'containers.settings.users.addUser.role.label',
        }),
        options: roleOptions,
        optionsAreLoading: false,
        getOptionLabel: option => {
          const group = groups.find(g => g.id === Number(option));
          return group
            ? intl.formatMessage({
                id: `containers.settings.users.roles.${group.name}`,
              })
            : option;
        },
        getChipLabel: value => {
          const group = groups.find(g => g.id === Number(value));
          return group
            ? intl.formatMessage({
                id: `containers.settings.users.roles.${group.name}`,
              })
            : value.toString();
        },
        valueGetter: (currentValue: UsersQuery) => currentValue.role,
        setFilter: value => setFilters({ role: value, page: '1' }),
        Component: FilterSelect,
      }),
      createSettings({
        filterKey: 'isActive',
        name: intl.formatMessage({
          id: 'containers.settings.users.status.filter.label',
        }),
        options: [...isActiveOptions],
        optionsAreLoading: false,
        getOptionLabel: getStatusLabel,
        getChipLabel: getStatusLabel,
        valueGetter: (currentValue: UsersQuery) => currentValue.isActive,
        setFilter: value => setFilters({ isActive: value, page: '1' }),
        Component: FilterSelect,
      }),
      createSettings({
        filterKey: 'queue',
        name: intl.formatMessage({
          id: 'features.queues.queueSelect.label',
        }),
        options: workspaceOptions ?? [],
        optionsAreLoading: isQueuesLoading,
        getOptionLabel: option =>
          queues?.find(q => q.id === Number(option))?.name ?? '',
        getChipLabel: value =>
          queues?.find(q => q.id === Number(value))?.name ?? '',
        valueGetter: (currentValue: UsersQuery) => currentValue.queue,
        validateFilter: value =>
          !isQueuesLoading && queues
            ? !queues.find(q => q.id === value)
            : false,
        setFilter: value => setFilters({ queue: value, page: '1' }),
        Component: FilterSelect,
      }),
    ],
    [
      intl,
      roleOptions,
      groups,
      setFilters,
      getStatusLabel,
      workspaceOptions,
      isQueuesLoading,
      queues,
    ]
  );

  return settings;
};
